import { Component } from 'react';
import DataTable from '../../../../../components/Datatable';
import Loading from '../../../../../components/Loading';
import { COLUMNS } from './keys';
import { getSortKeys, setAdditionalStyles } from '../../../../../helpers/table';
import { v4 as uuid } from 'uuid';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { colors, getRandomColor } from '../../../../../lib/colors';
import { Doughnut } from 'react-chartjs-2';
import { withTranslation } from 'react-i18next';
import { query } from './query';
import { normalizeDomain } from '../../../../../helpers/normalizeDomain';

ChartJS.register(ArcElement, Tooltip, Legend);

class ShareOfVoice extends Component {
    constructor() {
        super();

        this.onToggleOwnDomain = this.onToggleOwnDomain.bind(this);
        this.forceRun = this.forceRun.bind(this);
        this.componentDidUpdate = this.componentDidUpdate.bind(this);
        this.getData = this.getData.bind(this);

        this.state = {
            isOpenOwnDomain: false,
            data: [],
            loading: true,
        };
    }

    async getData({ force = false, alreadyInQueue = false }) {
        try {
            this.query = query({
                prompt_uids: this.props.prompt_uids,
                platforms: this.props.platforms,
            });

            this.setState({ loading: true });

            this.query.bind(this.setState.bind(this));
            const results = await this.query.update({ force, alreadyInQueue });
            const data = results?.map((x) => ({ id: uuid(), ...x }));
            this.setState({
                data,
                loading: false,
            });

            if (this.props.setOwnValue) {
                const ownDomainCitatioShare = this.state.data
                    ?.filter(
                        (item) =>
                            normalizeDomain(item.base_domain) ===
                            normalizeDomain(this.props.project?.own_domain),
                    )
                    ?.reduce((acc, item) => acc + item.citatioShare, 0);
                this.props.setOwnValue(
                    ownDomainCitatioShare ? ownDomainCitatioShare.toFixed(2) : 0,
                );
            }
        } catch (err) {
            console.log(err);
        }
    }

    forceRun() {
        this.getData({ force: true, alreadyInQueue: true });
    }

    async componentDidUpdate(prevProps) {
        if (
            !this.props.globalLoading &&
            this.props.globalLoading !== prevProps.globalLoading &&
            !this.props.prompt_uids?.length
        ) {
            this.setState({ data: [], loading: false });
        }
        if (
            JSON.stringify(prevProps.prompt_uids) !== JSON.stringify(this.props.prompt_uids) ||
            JSON.stringify(prevProps.platforms) !== JSON.stringify(this.props.platforms)
        ) {
            this.getData({});
        }

        if (this.props.updateAll !== prevProps.updateAll) {
            this.forceRun();
        }
    }

    searchFunction(data, search) {
        if (!search) return data;
        return data.filter((item) =>
            item.base_domain?.toLowerCase().includes(search.toLowerCase()),
        );
    }

    onToggleOwnDomain() {
        this.setState({
            isOpenOwnDomain: !this.state?.isOpenOwnDomain,
        });
    }

    calculatePieChartData(data) {
        const { t } = this.props;
        const dataForChart = data?.sort((a, b) => b.citatioShare - a.citatioShare) ?? [];
        let shownData = dataForChart;

        if (dataForChart.length > 20) {
            const first20 = dataForChart.slice(0, 20);
            const after20 = dataForChart.slice(20);
            const perncetOfAfter20 = after20.reduce((acc, item) => acc + item.citatioShare, 0);

            const other = {
                base_domain: t('ai_tracking.keyword_detail.other'),
                citatioShare: perncetOfAfter20,
            };

            shownData = [...first20, other];
        }

        if (shownData?.length === 1 && shownData?.[0]?.citatioShare === 0) {
            shownData[0].citatioShare = 1;
        }

        const pieData = {
            labels: shownData?.map((item) => item.base_domain),
            datasets: [
                {
                    label: t('ai_tracking.keyword_detail.share'),
                    data: shownData?.map((x) => x.citatioShare),
                    backgroundColor: shownData?.map((item, i) => colors[i] ?? getRandomColor()),
                },
            ],
        };

        return pieData;
    }

    render() {
        const { t } = this.props;

        const options = {
            maintainAspectRatio: false,
            cutout: '65%',
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: true,
                    text: t('ai_tracking.keyword_detail.share'),
                    padding: {
                        top: 10,
                        bottom: 10,
                    },
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            return `${context.label} : ${context.parsed?.toFixed(2)}%`;
                        },
                    },
                },
            },
        };

        const pieData = this.calculatePieChartData(this.state.data);

        return (
            <div className="card rounded-main ai-tracking-keyword-box-1 ">
                {this.state.loading && <Loading padding={10} />}
                {!this.state.loading && (
                    <div className="card-body">
                        {!this.state.data || !this.state.data?.length ? (
                            <div>
                                <div className="box-header mb-10">
                                    {t('ai_tracking.keyword_detail.share')}
                                </div>
                                <div className="mt-10">{t('global.no_data')}</div>
                            </div>
                        ) : (
                            <div className="row ai-tracking-keyword-box-citiation">
                                <div className="col-4 ai-tracking-keyword-box-citiation-chart">
                                    <div className="box-header mb-10">
                                        {t('ai_tracking.keyword_detail.share')}
                                    </div>
                                    <div className="ai-tracking-doughnut-chart">
                                        <Doughnut data={pieData} options={options} />
                                        <div className="ai-tracking-doughnut-chart-center">
                                            <div className="ai-tracking-doughnut-chart-center-text">
                                                {this.props.citiationShare ?? '0'}%
                                            </div>
                                            <div className="ai-tracking-doughnut-chart-center-subtext">
                                                {t('ai_tracking.global.own_citation_share')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-8 ai-tracking-keyword-box-citiation-table">
                                    <DataTable
                                        nodes={this.state.data}
                                        sortFns={getSortKeys(COLUMNS(t, this.props.project))}
                                        searchFunction={this.searchFunction}
                                        COLUMNS={COLUMNS(
                                            t,
                                            this.props.project,
                                            this.props.updateProject,
                                            this.state.data,
                                        )}
                                        defaultSortKey={'citatioShare'}
                                        hasTree
                                        customEmptyMessage={t('global.no_data')}
                                        additionalStyles={setAdditionalStyles(
                                            COLUMNS(t, this.props.project),
                                        )}
                                        fileName={t('ai_tracking.keyword_detail.share')}
                                        forceRun={this.forceRun}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default withTranslation()(ShareOfVoice);
